import { ChangeDetectionStrategy, Component, computed, effect, inject, linkedSignal } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  ButtonExpand,
  ButtonVariant,
  ButtonComponent,
  CheckboxComponent,
  Icon,
  ButtonIconLeftDirective,
  ToggleComponent,
  ButtonType,
} from '@circlek-global/lds-components';
import { AccordionGroupComponent } from 'src/app/lds-polyfill/accordion/accordion-group/accordion-group.component';
import { AccordionItemComponent } from 'src/app/lds-polyfill/accordion/accordion-item/accordion-item.component';

import { GtmCustomEventNameEnum } from '~components/gtm-script/gtm-custom-event-name.enum';
import { GtmCustomEventService } from '~components/gtm-script/gtm-custom-event.service';
import { FUEL_ICON_MAP } from '~constants/fuel-icons';
import { StationFuel, StationServiceModel } from '~models/station.model';
import { TranslatePipe } from '~pipes/translate.pipe';
import { BreakpointObserverService } from '~services/breakpoint-observer.service';
import { StationsService } from '~services/station.service';
import { StationDataStore } from '~state/station-data/station-data.store';

const SERVICES_LIMIT = 6;

@Component({
  selector: 'app-stations-filters-view',
  templateUrl: 'stations-filters-view.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AccordionItemComponent,
    TranslatePipe,
    CheckboxComponent,
    ReactiveFormsModule,
    FormsModule,
    ButtonComponent,
    ButtonIconLeftDirective,
    AccordionGroupComponent,
    ToggleComponent,
  ],
})
export class StationsFilterViewComponent {
  private readonly gtmCustomEventService = inject(GtmCustomEventService);
  private readonly stationDataStore = inject(StationDataStore);
  private readonly formBuilder = inject(FormBuilder);
  private readonly stationsService = inject(StationsService);
  private readonly breakpointObserverService = inject(BreakpointObserverService);
  private readonly services = this.stationDataStore.services;
  private readonly fuels = this.stationDataStore.fuels;
  protected readonly form = this.formBuilder.group({
    services: this.formBuilder.array<StationServiceModel & { checked: boolean }>([]),
    fuels: this.formBuilder.array<StationFuel & { checked: boolean }>([]),
    openNow: this.formBuilder.control(false, { nonNullable: true }),
  });
  protected readonly isDesktop = computed(() => this.breakpointObserverService.isDesktop());
  protected readonly showAllServices = linkedSignal(() => this.isDesktop());
  protected readonly servicesControl = computed(() => this.form.get('services') as FormArray);
  protected readonly servicesLimit = computed(() =>
    this.showAllServices() ? this.servicesControl().controls.length : SERVICES_LIMIT,
  );
  readonly isApplyButtonDisplayed = computed(() => this.form.dirty);
  protected readonly fuelsControl = computed(() => this.form.get('fuels') as FormArray);
  protected readonly openNowControl = computed(() => this.form.get('openNow') as FormControl);
  protected readonly ButtonVariant = ButtonVariant;
  protected readonly ButtonExpand = ButtonExpand;
  protected readonly ButtonType = ButtonType;
  protected readonly Icon = Icon;

  constructor() {
    effect(() => {
      this.handleFilterViewChange();
    });
  }

  clearAllFilters(): void {
    this.fuelsControl().controls.forEach((fuelGroup) => {
      fuelGroup.get('checked')?.setValue(false);
    });

    this.servicesControl().controls.forEach((serviceGroup) => {
      serviceGroup.get('checked')?.setValue(false);
    });

    this.openNowControl().setValue(false);
    this.form.markAsDirty();
  }

  applyFilters(): void {
    if (!this.form.dirty) return;

    const formValue = this.form.getRawValue();

    //List of services' names for request filtering; e.g. "NA_EVCHARGING"
    const services = formValue.services
      .filter((s) => s != null)
      .filter((s) => s != null && s.checked)
      .map((s) => s.name);

    //List of fuels' names for request filtering; e.g. "NA_GAS"
    const fuels = formValue.fuels
      .filter((f) => f != null)
      .filter((f) => f && f.checked)
      .map((f) => f.name);

    this.stationsService.filters.set({ services, fuels, openNow: formValue.openNow });
    this.stationsService.fetchStations().subscribe();
    this.stationsService.isFiltersViewOpened.set(false);
    this.form.markAsPristine();
    this.gtmCustomEventService.trackEvent(GtmCustomEventNameEnum.FilterUsage, {
      services: services.join(', '),
      fuels: fuels.join(', '),
      open_now: `${formValue.openNow}`,
    });
  }

  exitFiltersView(): void {
    this.stationsService.isFiltersViewOpened.set(false);
    this.form.markAsPristine();
  }

  onViewAllServices(): void {
    this.showAllServices.set(true);
  }

  onFilterGroupToggle(isOpened: boolean, filterGroupName: string): void {
    if (isOpened) {
      this.gtmCustomEventService.trackEvent(GtmCustomEventNameEnum.DropFilterGroup, {
        filter_group_name: filterGroupName,
      });
    }
  }

  private handleFilterViewChange(): void {
    const isOpen = this.stationsService.isFiltersViewOpened();

    this.stationsService.filters();

    if (!isOpen) {
      return;
    }

    if (this.servicesControl().length === 0 || this.fuelsControl().length === 0) {
      this.initServicesAndFuels();
    } else {
      this.syncFormWithFilters();
    }
  }

  private initServicesAndFuels(): void {
    const currentFilters = this.stationsService.filters();

    this.servicesControl().reset();
    this.services().forEach(({ displayName, name }) => {
      const isChecked = currentFilters.services.includes(name);

      this.servicesControl().push(
        this.formBuilder.group({
          displayName,
          name,
          checked: isChecked,
        }),
      );
    });

    this.fuelsControl().reset();
    this.fuels().forEach(({ displayName, name }) => {
      const isChecked = currentFilters.fuels.includes(name);
      const icon = FUEL_ICON_MAP[name];

      this.fuelsControl().push(
        this.formBuilder.group({
          displayName,
          name,
          checked: isChecked,
          icon,
        }),
      );
    });

    this.openNowControl().setValue(currentFilters.openNow);
  }

  private syncFormWithFilters(): void {
    const currentFilters = this.stationsService.filters();

    this.servicesControl().controls.forEach((serviceGroup) => {
      const name = serviceGroup.get('name')?.value;
      const shouldBeChecked = currentFilters.services.includes(name);
      const checkedControl = serviceGroup.get('checked');

      if (checkedControl && checkedControl.value !== shouldBeChecked) {
        checkedControl.setValue(shouldBeChecked, { emitEvent: false });
      }
    });

    this.fuelsControl().controls.forEach((fuelGroup) => {
      const name = fuelGroup.get('name')?.value;
      const shouldBeChecked = currentFilters.fuels.includes(name);
      const checkedControl = fuelGroup.get('checked');

      if (checkedControl && checkedControl.value !== shouldBeChecked) {
        checkedControl.setValue(shouldBeChecked, { emitEvent: false });
      }
    });

    if (this.openNowControl().value !== currentFilters.openNow) {
      this.openNowControl().setValue(currentFilters.openNow, { emitEvent: false });
    }
  }
}
